import { useEffect, useState } from "react";
import Logo from "components/logo";
import HeaderMenu from "components/HeaderMenu";
import SocialButtons from "components/SocialButtons";
import { Link } from "react-router-dom";

/* ── Founder CTA ─────────────────────────────────────────────────── */
function TalkToFounder() {
    return (
        <>
            <style>{`
                @keyframes founderGlow {
                    0%   { box-shadow: 0 0 0 3px rgba(109,220,255,0.35), 0 0 16px 4px rgba(126,96,248,0.45); }
                    50%  { box-shadow: 0 0 0 5px rgba(109,220,255,0.15), 0 0 28px 8px rgba(126,96,248,0.65); }
                    100% { box-shadow: 0 0 0 3px rgba(109,220,255,0.35), 0 0 16px 4px rgba(126,96,248,0.45); }
                }
                @keyframes founderRing1 {
                    0%   { transform: scale(1);   opacity: 0.6; }
                    100% { transform: scale(2.0); opacity: 0; }
                }
                @keyframes founderRing2 {
                    0%   { transform: scale(1);   opacity: 0.35; }
                    100% { transform: scale(2.6); opacity: 0; }
                }
                @keyframes founderShimmer {
                    0%,100% { background-position: 0% 50%; }
                    50%     { background-position: 100% 50%; }
                }
                @keyframes founderPulse {
                    0%,100% { opacity: 1; }
                    50%     { opacity: 0.6; }
                }
                .founder-btn:hover .founder-avatar { transform: scale(1.07); }
                .founder-btn:hover .founder-label  { color: #fff; }
            `}</style>

            <button
                className="founder-btn"
                onClick={() => window.dispatchEvent(new CustomEvent("talkToDavid"))}
                style={{
                    display: "flex", alignItems: "center", gap: 10,
                    background: "transparent", border: "none", cursor: "pointer",
                    padding: "5px 12px 5px 5px",
                    borderRadius: 28,
                    transition: "background 0.2s",
                    flexShrink: 0,
                }}
                title="Talk to founder"
                onMouseEnter={e => e.currentTarget.style.background = "rgba(109,220,255,0.05)"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}
            >
                <div style={{ position: "relative", flexShrink: 0, width: 36, height: 36 }}>
                    <div style={{
                        position: "absolute", inset: -5, borderRadius: "50%",
                        border: "1px solid rgba(126,96,248,0.35)",
                        animation: "founderRing2 3.2s ease-out infinite", animationDelay: "1.1s",
                    }} />
                    <div style={{
                        position: "absolute", inset: -3, borderRadius: "50%",
                        border: "1.5px solid rgba(109,220,255,0.5)",
                        animation: "founderRing1 2.4s ease-out infinite",
                    }} />
                    <div
                        className="founder-avatar"
                        style={{
                            width: 36, height: 36, borderRadius: "50%",
                            background: "linear-gradient(135deg, #6ddcff, #7e60f8, #6ddcff)",
                            backgroundSize: "200% 200%",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            fontFamily: "Manrope, sans-serif", fontWeight: 700,
                            fontSize: "0.78rem", color: "#fff",
                            animation: "founderGlow 2.8s ease-in-out infinite, founderShimmer 4s ease infinite",
                            transition: "transform 0.2s ease",
                            position: "relative", zIndex: 1,
                        }}
                    >
                        DC
                    </div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                    <span
                        className="founder-label"
                        style={{
                            fontFamily: "Manrope, sans-serif", fontSize: "0.75rem",
                            fontWeight: 600, color: "rgba(232,232,240,0.9)", lineHeight: 1.25,
                            whiteSpace: "nowrap", transition: "color 0.2s",
                        }}
                    >
                        Talk to founder
                    </span>
                    <span style={{
                        fontFamily: "Manrope, sans-serif", fontSize: "0.65rem",
                        color: "#6ddcff", letterSpacing: "0.04em", lineHeight: 1.5,
                        animation: "founderPulse 2.8s ease-in-out infinite",
                    }}>
                        ● Live · David Chiu
                    </span>
                </div>
            </button>
        </>
    );
}

/* ── Hamburger icon ──────────────────────────────────────────────── */
function HamburgerIcon({ open }) {
    const bar = (rotate, y) => ({
        display: "block",
        width: 22,
        height: 1.5,
        background: "rgba(255,255,255,0.85)",
        borderRadius: 2,
        transformOrigin: "center",
        transition: "transform 0.22s ease, opacity 0.22s ease",
        transform: rotate,
        opacity: y === "hidden" ? 0 : 1,
    });
    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 5.5, padding: 4 }}>
            <span style={bar(open ? "rotate(45deg) translate(5px, 5px)" : "none", "show")} />
            <span style={bar("none", open ? "hidden" : "show")} />
            <span style={bar(open ? "rotate(-45deg) translate(5px, -5px)" : "none", "show")} />
        </div>
    );
}

/* ── Mobile drawer nav links (grouped to mirror desktop dropdowns) ── */
const MOBILE_QUICK_LINKS = [
    { label: "Live Voice Agent", href: "/demo/voice-agent" },
    { label: "AI Front Desk", href: "/use-cases/ai-front-desk-for-homestays" },
    { label: "AI Companion", href: "/products/companion" },
    { label: "Emotion Models", href: "/products/emotion-models" },
];

const MOBILE_NAV_SECTIONS = [
    {
        title: "Demos",
        links: [
            { label: "All Demos", href: "/demo" },
            { label: "Build a Support Agent", href: "/demo/build-agent" },
            { label: "Live Voice Agent", href: "/demo/voice-agent" },
            { label: "Reads the Room (Agent EQ)", href: "/demo?tab=agent#demo-lab" },
            { label: "Tone That Adapts (Voice EQ)", href: "/demo?tab=voice#demo-lab" },
            { label: "Sounds Human (Article)", href: "/demo?tab=article#demo-lab" },
            { label: "Sees How You Feel (Perception)", href: "/demo?tab=perception#demo-lab" },
            { label: "Emotion Profile Tracking (Sample)", href: "/demo/emotion-profile" },
        ],
    },
    {
        title: "Industries",
        links: [
            { label: "Gaming & Virtual Worlds", href: "/#gaming" },
            { label: "Companions & Assistants", href: "/#companions" },
            { label: "Humanoid Robotics", href: "/#robotics" },
            { label: "Customer Support & Research", href: "/#support-cx" },
            { label: "Hospitality & Rental Stays", href: "/#hospitality-stays" },
        ],
    },
    {
        title: "Use Cases",
        links: [
            { label: "AI Front Desk For Homestays", href: "/use-cases/ai-front-desk-for-homestays" },
            { label: "Reduce Loneliness", href: "/use-cases/companionship" },
            { label: "Build Mood Awareness", href: "/use-cases/mood-awareness" },
            { label: "Write Like Yourself", href: "/use-cases/authentic-writing" },
            { label: "Make Sense of Your Day", href: "/use-cases/daily-reflection" },
            { label: "Reduce Support Escalations", href: "/use-cases/support-de-escalation" },
            { label: "Turn Every Interaction Into Insight", href: "/use-cases/interaction-insights" },
            { label: "Increase Player Retention", href: "/use-cases/player-retention" },
            { label: "Reduce Onboarding Drop-off", href: "/use-cases/onboarding-drop-off" },
            { label: "Improve Emotion Accuracy on Your Own Data", href: "/use-cases/domain-tuned-models" },
        ],
    },
    {
        title: "Products",
        links: [
            { label: "AI Companion", href: "/products/companion" },
            { label: "Emotion Profile Tracking", href: "/products/emotion-profile" },
            { label: "Self-Help & Mood Coach", href: "/products/mood-coach" },
            { label: "Text Humanizer", href: "/products/text-humanizer" },
            { label: "Voice Journal", href: "/products/voice-journal" },
            { label: "Emotion Models", href: "/products/emotion-models" },
            { label: "Multimodal Fusion", href: "/products/fusion" },
            { label: "Custom Model Training", href: "/products/custom-training" },
            { label: "Session Insights", href: "/products/session-insights" },
        ],
    },
    {
        title: "SDK & Platform",
        links: [
            { label: "Emotion SDK", href: "https://developer.aihumanity.io", external: true },
            { label: "VoicePlay", href: "https://voiceplay.aihumanity.io", external: true },
            { label: "AI Perception", href: "/perception" },
        ],
    },
];

const drawerLinkStyle = {
    fontFamily: "Manrope, sans-serif",
    fontSize: "15px",
    fontWeight: 500,
    color: "rgba(224,224,224,0.82)",
    textDecoration: "none",
    display: "block",
    padding: "13px 0",
    borderBottom: "1px solid rgba(255,255,255,0.06)",
    transition: "color 0.15s",
};

const drawerSectionTitleStyle = {
    fontFamily: "Manrope, sans-serif",
    fontSize: "11px",
    fontWeight: 700,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    color: "rgba(255,255,255,0.35)",
    padding: "16px 0 6px",
};

const quickActionStyle = {
    fontFamily: "Manrope, sans-serif",
    fontSize: "13px",
    fontWeight: 600,
    color: "#fff",
    textDecoration: "none",
    padding: "10px 14px",
    borderRadius: 999,
    border: "1px solid rgba(255,255,255,0.08)",
    background: "rgba(255,255,255,0.04)",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
};

function MobileAccordionSection({ title, links, onNavigate }) {
    return (
        <details style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
            <summary style={{
                listStyle: "none",
                cursor: "pointer",
                padding: "16px 0",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                fontFamily: "Manrope, sans-serif",
                fontSize: "11px",
                fontWeight: 700,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: "rgba(255,255,255,0.42)",
            }}>
                {title}
                <span style={{ fontSize: "14px", color: "rgba(255,255,255,0.62)" }}>+</span>
            </summary>
            <div style={{ paddingBottom: 12 }}>
                {links.map(({ label, href, external }) =>
                    external ? (
                        <a
                            key={href}
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={drawerLinkStyle}
                            onClick={onNavigate}
                        >
                            {label}
                        </a>
                    ) : (
                        <Link key={href} to={href} style={drawerLinkStyle} onClick={onNavigate}>
                            {label}
                        </Link>
                    )
                )}
            </div>
        </details>
    );
}

/* ── Main component ──────────────────────────────────────────────── */
export default function TopMenuBar() {
    const [mobileOpen, setMobileOpen] = useState(false);

    useEffect(() => {
        if (!mobileOpen) return undefined;

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        return () => {
            document.body.style.overflow = previousOverflow;
        };
    }, [mobileOpen]);

    return (
        <>
            <div
                style={{
                    position: "fixed",
                    top: 0, left: 0, right: 0,
                    zIndex: 100,
                    backdropFilter: "blur(20px)",
                    WebkitBackdropFilter: "blur(20px)",
                    background: "rgba(0,0,0,0.75)",
                    borderBottom: "1px solid rgba(255,255,255,0.07)",
                }}
            >
                {/* ── Nav bar row ── */}
                <header>
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            height: 64,
                            maxWidth: 1280,
                            margin: "0 auto",
                            padding: "0 48px",
                        }}
                        className="sm:px-5"
                    >
                        {/* Left: Logo + desktop nav */}
                        <div style={{ display: "flex", alignItems: "center", gap: 40 }}>
                            <Logo />
                            {/* Desktop-only nav links */}
                            <div className="md:hidden">
                                <HeaderMenu />
                            </div>
                        </div>

                        {/* Right: desktop CTA + socials / mobile hamburger */}
                        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                            {/* Desktop only */}
                            <div className="flex md:hidden" style={{ alignItems: "center", gap: 16 }}>
                                <TalkToFounder />
                                <div style={{ width: 1, height: 20, background: "rgba(255,255,255,0.12)" }} />
                            </div>

                            {/* Socials — always visible */}
                            <SocialButtons />

                            {/* Mobile-only hamburger */}
                            <button
                                className="hidden md:flex"
                                onClick={() => setMobileOpen(v => !v)}
                                style={{
                                    alignItems: "center",
                                    justifyContent: "center",
                                    background: "none",
                                    border: "none",
                                    cursor: "pointer",
                                    padding: 4,
                                    marginLeft: 4,
                                    borderRadius: 6,
                                }}
                                aria-label={mobileOpen ? "Close menu" : "Open menu"}
                            >
                                <HamburgerIcon open={mobileOpen} />
                            </button>
                        </div>
                    </div>
                </header>
            </div>

            {/* ── Mobile drawer ── */}
            {/* Rendered as a sibling of the backdrop-filtered header wrapper above,
                not a child of it — backdrop-filter creates a containing block for
                fixed-position descendants, which would otherwise collapse this
                overlay's `top: 64` against the header's own ~64px height instead
                of the viewport. */}
            <div
                className="hidden md:block"
                style={{
                    position: "fixed",
                    top: 64,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    opacity: mobileOpen ? 1 : 0,
                    pointerEvents: mobileOpen ? "auto" : "none",
                    transition: "opacity 0.24s ease",
                    background: "rgba(0,0,0,0.78)",
                    borderTop: mobileOpen ? "1px solid rgba(255,255,255,0.07)" : "none",
                    zIndex: 99,
                }}
                onClick={() => setMobileOpen(false)}
            >
                <div
                    style={{
                        position: "relative",
                        height: "100%",
                        overflowY: "auto",
                        overflowX: "hidden",
                        background: "rgba(4,4,8,0.98)",
                        boxShadow: "0 18px 48px rgba(0,0,0,0.55)",
                    }}
                    onClick={(event) => event.stopPropagation()}
                >
                    <div style={{ padding: "8px 24px 28px" }}>
                        <div style={drawerSectionTitleStyle}>Quick paths</div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 10 }}>
                            {MOBILE_QUICK_LINKS.map(({ label, href }) => (
                                <Link key={href} to={href} style={quickActionStyle} onClick={() => setMobileOpen(false)}>
                                    {label}
                                </Link>
                            ))}
                        </div>

                        <nav style={{ marginTop: 14 }}>
                            {MOBILE_NAV_SECTIONS.map(({ title, links }) => (
                                <MobileAccordionSection
                                    key={title}
                                    title={title}
                                    links={links}
                                    onNavigate={() => setMobileOpen(false)}
                                />
                            ))}
                        </nav>

                        <div style={{ marginTop: 12, display: "flex", gap: 16 }}>
                            <Link to="/benchmarks" style={{ ...drawerLinkStyle, borderBottom: "none", padding: "8px 0" }} onClick={() => setMobileOpen(false)}>
                                Benchmarks
                            </Link>
                            <Link to="/blog" style={{ ...drawerLinkStyle, borderBottom: "none", padding: "8px 0" }} onClick={() => setMobileOpen(false)}>
                                Blog
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
